import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../context/AuthContext'
import api from '../api/axios'
import { ChevronLeft, ChevronRight, Loader2, Calendar, Star } from 'lucide-react'

const ATTENDANCE_TYPES = [
  { value: 'present', label: 'Present', color: 'bg-green-100 text-green-700 border-green-200', group: 'attendance' },
  { value: 'casual', label: 'Casual Leave', color: 'bg-blue-100 text-blue-700 border-blue-200', group: 'leave' },
  { value: 'sick', label: 'Sick Leave', color: 'bg-purple-100 text-purple-700 border-purple-200', group: 'leave' },
  { value: 'permission', label: 'Permission', color: 'bg-orange-100 text-orange-700 border-orange-200', group: 'leave' },
  { value: 'halfday', label: 'Half Day', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', group: 'leave' },
  { value: 'half_day', label: 'Half Day', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', group: 'leave' },
  { value: 'other', label: 'Other Leave', color: 'bg-gray-100 text-gray-600 border-gray-200', group: 'leave' },
  { value: 'saturday', label: 'Saturday Presence', color: 'bg-cyan-100 text-cyan-700 border-cyan-200', group: 'incentive' },
  { value: 'night_shift', label: 'Night Shift', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', group: 'incentive' },
  { value: 'double_wages', label: 'Double Wages', color: 'bg-pink-100 text-pink-700 border-pink-200', group: 'incentive' },
]

const TYPE_MAP = Object.fromEntries(ATTENDANCE_TYPES.map(t => [t.value, t]))

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay()
}

function toDateKey(date: Date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ── Mark Attendance Modal ────────────────────────────────────────────────────
function MarkAttendanceModal({ date, userId, existing, onClose, onMarked }: {
  date: string, userId: string, existing: any[], onClose: () => void, onMarked: () => void
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  const { user } = useAuth()
  const canMarkIncentive = ['admin', 'hr'].includes(user?.roleName || '')
  const existingTypes = existing.map((r: any) => r.type)
  const [type, setType] = useState('present')
  const [note, setNote] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      await api.post('/attendance', { userId, date, type, note })
      onMarked()
      onClose()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to mark attendance')
    }
    setSaving(false)
  }

  const handleDelete = async (id: string) => {
    setSaving(true)
    try {
      await api.delete(`/attendance/${id}`)
      onMarked()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Cannot delete this record')
    }
    setSaving(false)
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f]">
          <h2 className="font-display font-bold text-navy-900 dark:text-white text-base">
            Mark Attendance — {new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <div>
            <label className="label">Attendance Type</label>
            <div className="space-y-2">
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Attendance</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ATTENDANCE_TYPES.filter(t => t.group === 'attendance').map(t => (
                  <button key={t.value} onClick={() => setType(t.value)}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                      type === t.value ? t.color + ' ring-2 ring-offset-1 ring-orange-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide pt-1">Leave</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ATTENDANCE_TYPES.filter(t => t.group === 'leave').map(t => (
                  <button key={t.value} onClick={() => setType(t.value)}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                      type === t.value ? t.color + ' ring-2 ring-offset-1 ring-orange-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
              {canMarkIncentive && (
              <>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-wide pt-1 flex items-center gap-1"><Star className="w-3 h-3" /> Special Incentives</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ATTENDANCE_TYPES.filter(t => t.group === 'incentive').map(t => (
                  <button key={t.value} onClick={() => setType(t.value)}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                      type === t.value ? t.color + ' ring-2 ring-offset-1 ring-orange-300' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
              </>
              )}
            </div>
          </div>
          <div>
            <label className="label">Note <span className="text-gray-400 font-normal">(optional)</span></label>
            <input type="text" className="input text-sm" placeholder="Add a note..." value={note} onChange={e => setNote(e.target.value)} />
          </div>
          {error && <p className="text-red-500 text-sm">{error}</p>}
        </div>
        {/* Existing records list with delete */}
        {existing.length > 0 && (
          <div className="px-6 pb-3 space-y-1.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Marked Today</p>
            {existing.map((r: any) => {
              const ti = TYPE_MAP[r.type]
              return (
                <div key={r.id} className="flex items-center justify-between px-3 py-2 rounded-xl bg-gray-50 dark:bg-[#112240]">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${ti?.color || 'bg-gray-100 text-gray-600'}`}>
                    {ti?.label || r.type}
                  </span>
                  {!r.leaveApplicationId && (
                    <button onClick={() => handleDelete(r.id)} disabled={saving}
                      className="text-xs text-red-400 hover:text-red-600 transition-colors">
                      Remove
                    </button>
                  )}
                  {r.leaveApplicationId && (
                    <span className="text-[10px] text-orange-400">
                      via leave{r.halfDaySession ? ` · ${r.halfDaySession}` : ''}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        )}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-[#1e3a5f] flex justify-end gap-3">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary text-sm flex items-center gap-2">
            {saving && <Loader2 size={13} className="animate-spin" />}
            Add
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}


// ── Main Page ────────────────────────────────────────────────────────────────
export default function AttendancePage() {
  const { user, hasPermission } = useAuth()
  const canMarkAttendance = hasPermission('mark_attendance')
  const canViewAll = hasPermission('view_attendance_all')

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(today.getMonth())
  const [records, setRecords] = useState<Record<string, any[]>>({})
  const [loading, setLoading] = useState(true)
  const [holidays, setHolidays] = useState<any[]>([])
  const [showHolidayForm, setShowHolidayForm] = useState(false)
  const [holidayForm, setHolidayForm] = useState({ date: '', name: '' })
  const [savingHoliday, setSavingHoliday] = useState(false)
  const [staffList, setStaffList] = useState<any[]>([])
  const [selectedUserId, setSelectedUserId] = useState<string>(user?.userId?.toString() || '')
  const [markingDate, setMarkingDate] = useState<string | null>(null)
  const [calendarView, setCalendarView] = useState<'my' | 'team'>('my')
  const [teamRecords, setTeamRecords] = useState<Record<string, any[]>>({})
  const [teamLoading, setTeamLoading] = useState(false)

  const fetchAttendance = async () => {
    setLoading(true)
    try {
      const month = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`
      const params: any = { month }
      if (canViewAll && selectedUserId) params.userId = selectedUserId
      const res = await api.get('/attendance', { params })
      const map: Record<string, any[]> = {}
      for (const r of res.data) {
        const key = r.date.slice(0, 10)
        if (!map[key]) map[key] = []
        map[key].push(r)
      }
      setRecords(map)
    } catch {}
    setLoading(false)
  }

  const fetchStaffList = async () => {
    try {
      const res = await api.get('/attendance/staff-list')
      setStaffList(res.data)
      if (!selectedUserId && res.data.length > 0) {
        setSelectedUserId(user?.userId?.toString() || res.data[0].id)
      }
    } catch {}
  }

  const fetchTeamAttendance = async () => {
  setTeamLoading(true)
  try {
    const month = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}`
    const res = await api.get('/attendance/team', { params: { month } })
    setTeamRecords(res.data)
  } catch {}
  setTeamLoading(false)
  }

  useEffect(() => {
    if (canViewAll) fetchStaffList()
  }, [])

  useEffect(() => {
    fetchAttendance()
    if (canViewAll) fetchTeamAttendance()
  }, [currentYear, currentMonth, selectedUserId])

  useEffect(() => {
    api.get(`/attendance/holidays?month=${currentMonth + 1}&year=${currentYear}`)
      .then(res => setHolidays(res.data))
      .catch(() => {})
  }, [currentYear, currentMonth])

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentYear(y => y - 1); setCurrentMonth(11) }
    else setCurrentMonth(m => m - 1)
  }

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentYear(y => y + 1); setCurrentMonth(0) }
    else setCurrentMonth(m => m + 1)
  }

  const daysInMonth = getDaysInMonth(currentYear, currentMonth)
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth)

  // Summary counts
  const allRecords = Object.values(records).flat()
  const summary = ATTENDANCE_TYPES.reduce((acc, t) => {
    acc[t.value] = allRecords.filter(r => r.type === t.value).length
    return acc
  }, {} as Record<string, number>)

  const selectedStaff = staffList.find(s => s.id === selectedUserId)
  const holidayMap = Object.fromEntries(holidays.map(h => [h.date.split('T')[0], h]))

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy-900">Attendance</h1>
          <p className="text-sm text-gray-400 mt-0.5">{calendarView === 'team' ? 'Team absence calendar' : 'Monthly attendance record'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {canViewAll && (
            <div className="flex bg-gray-100 dark:bg-[#112240] rounded-xl p-1 gap-1">
              <button onClick={() => setCalendarView('my')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${calendarView === 'my' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700'}`}>
                My Attendance
              </button>
              <button onClick={() => setCalendarView('team')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${calendarView === 'team' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700'}`}>
                Team Calendar
              </button>
            </div>
          )}
          {canViewAll && staffList.length > 0 && calendarView === 'my' && (
            <select className="input w-full sm:w-52 text-sm" value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}>
              {staffList.map(s => (
                <option key={s.id} value={s.id}>{s.name} ({s.roleLabel})</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Month navigator */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors">
          <ChevronLeft size={20} />
        </button>
        <h2 className="text-lg font-display font-bold text-navy-900">
          {MONTHS[currentMonth]} {currentYear}
        </h2>
        <button onClick={nextMonth} className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors"
          disabled={currentYear === today.getFullYear() && currentMonth === today.getMonth()}>
          <ChevronRight size={20} />
        </button>
      </div>

      {calendarView === 'team' && canViewAll ? (
        <div className="bg-white dark:bg-[#16304f] rounded-2xl shadow-sm border border-gray-100 dark:border-[#1e3a5f] overflow-hidden">
          {teamLoading ? (
            <div className="py-20 text-center text-gray-400"><Loader2 size={24} className="animate-spin mx-auto" /></div>
          ) : (
            <>
              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-gray-100 dark:border-[#1e3a5f]">
                {DAYS.map(d => (
                  <div key={d} className="py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">{d}</div>
                ))}
              </div>
              {/* Calendar grid */}
              <div className="grid grid-cols-7">
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`empty-${i}`} className="min-h-[80px] border-b border-r border-gray-50 dark:border-[#1e3a5f] bg-gray-50/30 dark:bg-[#0d1e35]/30" />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1
                  const dateStr = toDateKey(new Date(currentYear, currentMonth, day))
                  const isToday = dateStr === toDateKey(today)
                  const absences = teamRecords[dateStr] || []
                  const holiday = holidays.find(h => h.date.slice(0, 10) === dateStr)
                  const isSunday = new Date(currentYear, currentMonth, day).getDay() === 0
                  const absenceCount = absences.length
                  const heatBg = absenceCount === 0 ? '' 
                    : absenceCount === 1 ? 'bg-orange-50 dark:bg-orange-900/10'
                    : absenceCount === 2 ? 'bg-orange-100 dark:bg-orange-900/20'
                    : absenceCount === 3 ? 'bg-orange-200 dark:bg-orange-900/30'
                    : 'bg-orange-300 dark:bg-orange-900/50'
                  return (
                    <div key={day} className={`min-h-[80px] border-b border-r border-gray-100 dark:border-[#1e3a5f] p-1.5 ${isSunday && absenceCount === 0 ? 'bg-gray-50/50 dark:bg-[#0d1e35]/50' : ''} ${holiday && absenceCount === 0 ? 'bg-orange-50/30 dark:bg-orange-900/5' : ''} ${heatBg}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-semibold w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-orange-500 text-white' : 'text-gray-500 dark:text-gray-400'}`}>{day}</span>
                        {holiday && <span className="text-[9px] text-orange-500 truncate max-w-[50px]">{holiday.name}</span>}
                      </div>
                      <div className="flex flex-wrap gap-0.5">
                        {absences.map((a: any, idx: number) => {
                          const typeInfo = TYPE_MAP[a.type]
                          const initials = a.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
                          const colors: Record<string, string> = {
                            casual: 'bg-blue-100 text-blue-700',
                            sick: 'bg-purple-100 text-purple-700',
                            permission: 'bg-orange-100 text-orange-700',
                            halfday: 'bg-yellow-100 text-yellow-700',
                            half_day: 'bg-yellow-100 text-yellow-700',
                            other: 'bg-gray-100 text-gray-600',
                          }
                          const colorClass = colors[a.type] || 'bg-gray-100 text-gray-600'
                          return (
                            <div key={idx} className="relative group/tip">
                              <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${colorClass} cursor-default block`}>
                                {initials}
                              </span>
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-50 hidden group-hover/tip:block pointer-events-none">
                                <div className="bg-navy-900 dark:bg-[#0d1e35] text-white text-[10px] font-medium px-2 py-1 rounded-lg whitespace-nowrap shadow-lg">
                                  {a.name} — {typeInfo?.label || a.type}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
              {/* Legend */}
              <div className="px-4 py-3 border-t border-gray-100 dark:border-[#1e3a5f] flex flex-wrap gap-3">
                {[
                  { label: 'Casual Leave', color: 'bg-blue-100 text-blue-700' },
                  { label: 'Sick Leave', color: 'bg-purple-100 text-purple-700' },
                  { label: 'Permission', color: 'bg-orange-100 text-orange-700' },
                  { label: 'Half Day', color: 'bg-yellow-100 text-yellow-700' },
                  { label: 'Other', color: 'bg-gray-100 text-gray-600' },
                ].map(l => (
                  <div key={l.label} className="flex items-center gap-1">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${l.color}`}>AB</span>
                    <span className="text-xs text-gray-400">{l.label}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
      <>
      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-5">
        {ATTENDANCE_TYPES.filter(t => summary[t.value] > 0).map(t => (
          <span key={t.value} className={`px-3 py-1 rounded-full text-xs font-medium border ${t.color}`}>
            {t.label}: {summary[t.value]}
          </span>
        ))}
        {Object.keys(records).length === 0 && !loading && (
          <span className="text-xs text-gray-400">No records this month</span>
        )}
      </div>

      {/* Calendar */}
      <div className="bg-white dark:bg-[#16304f] rounded-2xl shadow-sm border border-gray-100 dark:border-[#1e3a5f] overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {DAYS.map(d => (
            <div key={d} className="py-3 text-center text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {d}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="py-20 text-center text-gray-400">
            <Loader2 size={24} className="animate-spin mx-auto mb-2" />
            Loading attendance...
          </div>
        ) : (
          <div className="grid grid-cols-7">
            {/* Empty cells for first day offset */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[80px] border-b border-r border-gray-50 bg-gray-50/30" />
            ))}

            {/* Day cells */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1
              const date = new Date(currentYear, currentMonth, day)
              const dateKey = toDateKey(date)
              const dayRecords = records[dateKey] || []
              const isToday = dateKey === toDateKey(new Date())
              const isFuture = date > today
              const isWeekend = date.getDay() === 0 || date.getDay() === 6
              const holiday = holidayMap[dateKey]

              return (
                <div key={day}
                  onClick={() => {
                    if (!isFuture && canMarkAttendance) setMarkingDate(dateKey)
                  }}
                  className={`min-h-[80px] border-b border-r border-gray-100 p-2 flex flex-col transition-colors
                    ${!isFuture && canMarkAttendance ? 'cursor-pointer hover:bg-orange-50/30' : ''}
                    ${isWeekend ? 'bg-gray-50/50' : ''}
                    ${isFuture ? 'opacity-40' : ''}
                  `}>
                  <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full
                    ${isToday ? 'bg-orange-500 text-white' : 'text-gray-600'}`}>
                    {day}
                  </span>
                  {holiday && (
                    <div className="mt-0.5 px-1.5 py-0.5 rounded-lg text-[9px] font-medium bg-red-50 text-red-500 border border-red-100 truncate">
                      🎌 {holiday.name}
                    </div>
                  )}
                  {dayRecords.map((r: any) => {
                    const ti = TYPE_MAP[r.type]
                    return ti ? (
                      <div key={r.id} className={`mt-0.5 px-1.5 py-0.5 rounded-lg text-[10px] font-medium border ${ti.color} truncate`}>
                        {ti.label}
                      </div>
                    ) : null
                  })}
                  {dayRecords.some((r: any) => r.note) && (
                    <p className="text-[9px] text-gray-400 mt-0.5 truncate">{dayRecords.find((r: any) => r.note)?.note}</p>
                  )}
                  {dayRecords.some((r: any) => r.leaveApplicationId) && (
                      <span className="text-[9px] text-orange-400 mt-0.5">
                        via leave{dayRecords.find((r: any) => r.halfDaySession)?.halfDaySession ? ` · ${dayRecords.find((r: any) => r.halfDaySession)?.halfDaySession}` : ''}
                      </span>
                    )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Holidays section */}
      <div className="mt-6 card overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
          <div>
            <p className="font-display font-semibold text-navy-900 dark:text-white">🎌 Holidays — {MONTHS[currentMonth]} {currentYear}</p>
            <p className="text-xs text-gray-400">{holidays.length} holiday{holidays.length !== 1 ? 's' : ''} this month</p>
          </div>
          {canMarkAttendance && (
            <button onClick={() => setShowHolidayForm(v => !v)}
              className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg font-medium transition-colors">
              {showHolidayForm ? 'Cancel' : '+ Add Holiday'}
            </button>
          )}
        </div>

        {showHolidayForm && (
          <div className="px-5 py-4 border-b border-orange-100 dark:border-orange-500/20 bg-orange-50 dark:bg-[#1a2a40] space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label">Date *</label>
                <input type="date" className="input text-sm" value={holidayForm.date}
                  onChange={e => setHolidayForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div>
                <label className="label">Holiday Name *</label>
                <input type="text" className="input text-sm" placeholder="e.g. Pongal" value={holidayForm.name}
                  onChange={e => setHolidayForm(f => ({ ...f, name: e.target.value }))} />
              </div>
            </div>
            <button onClick={async () => {
              if (!holidayForm.date || !holidayForm.name.trim()) return
              setSavingHoliday(true)
              try {
                await api.post('/attendance/holidays', holidayForm)
                setHolidayForm({ date: '', name: '' })
                setShowHolidayForm(false)
                const res = await api.get(`/attendance/holidays?month=${currentMonth + 1}&year=${currentYear}`)
                setHolidays(res.data)
              } catch (e: any) {
                alert(e.response?.data?.error || 'Failed to add holiday')
              }
              setSavingHoliday(false)
            }} disabled={savingHoliday || !holidayForm.date || !holidayForm.name.trim()}
              className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors">
              {savingHoliday ? 'Saving...' : 'Save Holiday'}
            </button>
          </div>
        )}

        {holidays.length === 0 ? (
          <div className="py-8 text-center text-gray-400 text-sm">No holidays this month</div>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
            {holidays.map(h => (
              <div key={h.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-navy-800 dark:text-white">{h.name}</p>
                  <p className="text-xs text-gray-400">{new Date(h.date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                </div>
                {canMarkAttendance && (
                  <button onClick={async () => {
                    if (!window.confirm('Delete this holiday?')) return
                    await api.delete(`/attendance/holidays/${h.id}`)
                    setHolidays(prev => prev.filter(x => x.id !== h.id))
                  }} className="text-xs text-red-400 hover:text-red-600 transition-colors">Delete</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-4">
        {ATTENDANCE_TYPES.filter((t, i, arr) => arr.findIndex(x => x.label === t.label) === i).map(t => (
          <div key={t.value} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-sm border ${t.color}`} />
            <span className="text-xs text-gray-500">{t.label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm border bg-red-50 border-red-100" />
          <span className="text-xs text-gray-500">Holiday</span>
        </div>
      </div>

      {/* Mark attendance modal */}
      {markingDate && (
        <MarkAttendanceModal
          date={markingDate}
          userId={selectedUserId || user?.userId?.toString() || ''}
          existing={records[markingDate] || []}
          onClose={() => setMarkingDate(null)}
          onMarked={() => { setMarkingDate(null); fetchAttendance() }}
        />
      )}
      </>
      )}
    </div>
  )
}